import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useProfileStore } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, AlertTriangle, Check } from 'lucide-react';
import CurrencySelector from '../components/CurrencySelector';

// Predefined avatar options
const AVATAR_OPTIONS = [
  '/avatars/char.png',
  '/avatars/bulba.png',
  '/avatars/squir.png'
];

interface Profile {
  id: string;
  username: string | null;
  avatar_url: string | null;
  email: string | null;
}

export default function ProfilePage() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [username, setUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const setGlobalProfile = useProfileStore(state => state.setProfile);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate('/auth');
        return;
      }
      setUser(session.user);
      getProfile(session.user.id).finally(() => {
        setInitialized(true);
      });
    });
  }, [navigate]);

  async function getProfile(userId: string) {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        throw profileError;
      }

      if (profile) {
        setProfile(profile);
        setUsername(profile.username || '');
        setAuthProvider(profile.auth_provider || null);
        const currentAvatar = profile.avatar_url;
        setAvatarUrl(currentAvatar);
        setSelectedAvatar(currentAvatar);
        setGlobalProfile(profile);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    setHasUnsavedChanges(true);
  };

  async function updateAvatar() {
    if (!selectedAvatar) return;
    
    try {
      setError(null);
      setLoading(true);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: selectedAvatar })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      setAvatarUrl(selectedAvatar);
      setGlobalProfile({ ...profile!, avatar_url: selectedAvatar });
      setHasUnsavedChanges(false);
      setShowAvatarSelector(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error uploading avatar');
    } finally {
      setLoading(false);
    }
  }

  async function updateProfile() {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('profiles')
        .update({ username })
        .eq('id', user.id);

      if (error) throw error;
      setHasUnsavedChanges(false);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }

  async function deleteAccount() {
    if (!user || deleteConfirmText !== user.email) return;
    
    try {
      setLoading(true);
      setError(null);

      // Delete user account
      const { error } = await supabase.auth.admin.deleteUser(user.id);

      if (error) throw error;

      // Sign out and redirect
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error deleting account');
    } finally {
      setLoading(false);
    }
  }

  if (loading && !initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-white border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Profile Settings</h1>

        <div className="bg-white/5 border border-white/10 rounded-lg p-6 space-y-8">
          {/* Avatar Section */}
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-white/10 overflow-hidden border-2 border-white/10">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40">
                    No Image
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowAvatarSelector(true)}
                className="absolute bottom-0 right-0 p-2 bg-white/10 rounded-full cursor-pointer hover:bg-white/20 transition-colors text-white group"
              >
                <span className="hidden group-hover:block">Change</span>
                <span className="block group-hover:hidden">Edit</span>
              </button>
            </div>
            <div>
              <h3 className="text-lg font-medium">Profile Picture</h3>
              <p className="text-sm text-white/60">
                Choose your avatar from our collection
              </p>
            </div>
          </div>

          {/* Avatar Selector Modal */}
          {showAvatarSelector && (
            <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">Choose an Avatar</h3>
                  <button
                    onClick={() => {
                      setShowAvatarSelector(false);
                      setSelectedAvatar(avatarUrl);
                    }}
                    className="text-white/60 hover:text-white"
                  >
                    Close
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {AVATAR_OPTIONS.map((url, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedAvatar(url)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                        selectedAvatar === url
                          ? 'border-white ring-2 ring-white/25'
                          : 'border-white/10 hover:border-white/25'
                      }`}
                    >
                      <img
                        src={url}
                        alt={`Pokemon avatar ${url.split('/').pop()?.split('.')[0]}`}
                        className="w-full h-full object-cover"
                      />
                      {selectedAvatar === url && (
                        <div className="absolute inset-0 bg-white/10 flex items-center justify-center">
                          <Check className="text-white" size={24} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={updateAvatar}
                    disabled={!selectedAvatar || selectedAvatar === avatarUrl || loading}
                    className="flex-1 py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Saving...' : 'Save Avatar'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAvatarSelector(false);
                      setSelectedAvatar(avatarUrl);
                    }}
                    className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Profile Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-medium mb-3">Currency Preference</h3>
              <p className="text-sm text-white/60 mb-4">
                Choose your preferred currency for card prices.
              </p>
              <CurrencySelector />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/60 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60"
              />
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-white/60 mb-1">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={handleUsernameChange}
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-white/25"
              />
            </div>

            {hasUnsavedChanges && (
              <div className="text-yellow-500 text-sm bg-yellow-500/10 p-3 rounded-lg">
                You have unsaved changes
              </div>
            )}

            {error && (
              <div className="text-red-500 text-sm bg-red-500/10 p-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              onClick={updateProfile}
              disabled={loading || !hasUnsavedChanges}
              className="w-full py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
            </button>
          </div>

          {/* Authentication Provider Info */}
          <div className="mt-4 p-4 bg-white/5 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Account Details</h3>
            <div className="space-y-2">
              <p className="text-white/60">
                Sign in method:{' '}
                <span className="text-white">
                  {authProvider === 'discord' ? 'Discord' :
                   authProvider === 'google' ? 'Google' :
                   authProvider === 'email' ? 'Email and Password' :
                   'Unknown'}
                </span>
              </p>
              <p className="text-white/60">
                Account created: {profile?.created_at && (
                  <span className="text-white">
                    {new Date(profile.created_at).toLocaleDateString()}
                  </span>
                )}
              </p>
              <p className="text-white/60">
                Last sign in: {profile?.last_sign_in && (
                  <span className="text-white">
                    {new Date(profile.last_sign_in).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Delete Account Section */}
          <div className="pt-8 border-t border-white/10">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Trash2 className="text-red-500 mt-1" size={20} />
                <div>
                  <h3 className="text-lg font-medium text-red-500">Delete Account</h3>
                  <p className="text-sm text-white/60">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                </div>
              </div>

              {showDeleteConfirm ? (
                <div className="space-y-4">
                  <div className="bg-red-500/10 p-4 rounded-lg flex items-start space-x-3">
                    <AlertTriangle className="text-red-500 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-red-500">
                        This action is permanent and cannot be undone. Please type your email address
                        (<strong>{user?.email}</strong>) to confirm deletion.
                      </p>
                    </div>
                  </div>

                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Enter your email to confirm"
                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-red-500/25 text-white focus:outline-none focus:ring-2 focus:ring-red-500/25"
                  />

                  <div className="flex space-x-3">
                    <button
                      onClick={deleteAccount}
                      disabled={deleteConfirmText !== user?.email}
                      className="px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-500 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm Delete
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-500 font-medium transition-colors"
                >
                  Delete Account
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}